import { generatePdfUint8Array } from './pdfUtilities';
import { PDFDocument } from 'pdf-lib';
import { getFont, toPdfStr, splitConstructSub } from '../utils';
import { createPdf } from '../components/custom_formats/CustomEstimate1';
import { TEstimateBaseItem } from '../types/estimate/estimate';
import { calcAllSellingPrice } from '../utils/calcRules';
import {
  ESTIMATE_COLOR_GRAY_TENANT_IDS,
  ESTIMATE_ITEM_MAX_PER_PDF_PAGE,
  CUSTOM_ESTIMATE_STAMP_PLACE
} from '../const/Const';
import { Template } from '@pdfme/common';
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist';
import 'pdfjs-dist/legacy/build/pdf.worker.entry';

export const estimatePdf = async (data: any) => {
  const font = await getFont();

  const page1: any = await getPage1(data, font);
  const page2: any = await getPage2(data, font);

  const pdfDoc1 = await PDFDocument.load(page1);
  const pdfDoc2 = await PDFDocument.load(page2);

  const mergedPdf = await PDFDocument.create();
  const copiedPage1 = await mergedPdf.copyPages(
    pdfDoc1,
    pdfDoc1.getPageIndices()
  );
  copiedPage1.forEach(page => mergedPdf.addPage(page));
  const copiedPage2 = await mergedPdf.copyPages(
    pdfDoc2,
    pdfDoc2.getPageIndices()
  );
  copiedPage2.forEach(page => mergedPdf.addPage(page));

  const exportPdf = await mergedPdf.save();

  const blob = new Blob([exportPdf], { type: 'application/pdf' });
  return blob;
};

const getPage1 = async (data: any, font: any) => {
  // 初回読み込みの容量を減らすため動的インポート
  const basePdf1 = await import('../assets/estimates_base_pdf1');
  const basePdfPinkWithoutConfirmStamp = await import(
    '../assets/estimates_base_pdf_pink_without_confirm_stamp'
  ); // 確認印欄なし, 御見積条件pink
  const basePdfGrayWithoutConfirmStamp = await import(
    '../assets/estimates_base_pdf_gray_without_confirm_stamp'
  ); // 確認印欄なし, 御見積条件gray
  let basePdf;
  const mode = process.env
    .REACT_APP_MODE as keyof typeof ESTIMATE_COLOR_GRAY_TENANT_IDS;
  const grayColorTenantIds = ESTIMATE_COLOR_GRAY_TENANT_IDS[mode];
  if (data.confirmation_stamp == 1) {
    // 確認印なし
    if (
      grayColorTenantIds !== undefined &&
      grayColorTenantIds.includes(data.tenant_id)
    ) {
      basePdf = basePdfGrayWithoutConfirmStamp.basePdf;
    } else {
      basePdf = basePdfPinkWithoutConfirmStamp.basePdf;
    }
  } else {
    // 確認印あり
    basePdf = basePdf1.basePdf;
  }

  const template: Template = {
    basePdf: basePdf,
    schemas: [
      {
        client_name: {
          // 請求先顧客名
          type: 'text',
          position: {
            x: 38.1,
            y: 53.62
          },
          width: 118.49,
          height: 10.97,
          alignment: 'center',
          fontSize: 15,
          characterSpacing: 0,
          lineHeight: 1.5
        },
        billing_total_label: {
          // 本体工事金額(ラベル)
          type: 'text',
          position: {
            x: 37,
            y: 88
          },
          width: 65.15,
          height: 10.7,
          alignment: 'left',
          fontSize: 10,
          characterSpacing: 0,
          lineHeight: 1.5
        },
        billing_total: {
          // 本体工事金額
          type: 'text',
          position: {
            x: 80.65,
            y: 86
          },
          width: 65.15,
          height: 10.7,
          alignment: 'left',
          fontSize: 15,
          characterSpacing: 0,
          lineHeight: 1.5
        },
        total_price_label: {
          // 税込合計金額(ラベル)
          type: 'text',
          position: {
            x: 53,
            y: 100.9
          },
          width: 33.13,
          height: 4.87,
          alignment: 'left',
          fontSize: 8,
          characterSpacing: 0,
          lineHeight: 1
        },
        total_price: {
          // 税込合計金額
          type: 'text',
          position: {
            x: 76.36,
            y: 100
          },
          width: 33.13,
          height: 4.87,
          alignment: 'left',
          fontSize: 12,
          characterSpacing: 0,
          lineHeight: 1
        },
        tax: {
          // 消費税
          type: 'text',
          position: {
            x: 133,
            y: 100
          },
          width: 33.13,
          height: 4.87,
          alignment: 'left',
          fontSize: 12,
          characterSpacing: 0,
          lineHeight: 1
        },
        construct_sub: {
          // 件名
          type: 'text',
          position: {
            x: 54.5,
            y: 107.68
          },
          width: 117,
          height: 8.85,
          alignment: 'center',
          verticalAlignment: 'middle',
          dynamicFontSize: {
            max: 15,
            min: 11
          }
        },
        summary: {
          // 摘要
          type: 'text',
          position: {
            x: 54.45,
            y: 117.89
          },
          width: 114.37,
          height: 15.98,
          alignment: 'left',
          fontSize: 11,
          characterSpacing: 0,
          lineHeight: 1
        },
        site_address: {
          // 現場住所
          type: 'text',
          position: {
            x: 63,
            y: 153.03
          },
          width: 110.1,
          height: 6.2,
          alignment: 'left',
          fontSize: 11,
          characterSpacing: 0,
          lineHeight: 1
        },
        site_address_under_30: {
          // 現場住所(2行出力)
          type: 'text',
          position: {
            x: 63,
            y: 151.03
          },
          width: 105.1,
          height: 6.2,
          alignment: 'left',
          fontSize: 9,
          characterSpacing: 0,
          lineHeight: 1
        },
        site_address_over_30: {
          // 現場住所(2行出力)
          type: 'text',
          position: {
            x: 63,
            y: 155.03
          },
          width: 105.1,
          height: 6.2,
          alignment: 'left',
          fontSize: 9,
          characterSpacing: 0,
          lineHeight: 1
        },
        start_date: {
          // 工事予定日
          type: 'text',
          position: {
            x: 63,
            y: 159.38
          },
          width: 105.1,
          height: 6.2,
          alignment: 'left',
          fontSize: 11,
          characterSpacing: 0,
          lineHeight: 1
        },
        end_date: {
          // 引渡予定日
          type: 'text',
          position: {
            x: 63,
            y: 167
          },
          width: 105.1,
          height: 6.2,
          alignment: 'left',
          fontSize: 11,
          characterSpacing: 0,
          lineHeight: 1
        },
        expire_date: {
          // 見積有効期限
          type: 'text',
          position: {
            x: 63,
            y: 174.35
          },
          width: 105.1,
          height: 6.2,
          alignment: 'left',
          fontSize: 11,
          characterSpacing: 0,
          lineHeight: 1
        },
        terms_of_trade: {
          // 取引条件
          type: 'text',
          position: {
            x: 63,
            y: 181.7
          },
          width: 105.1,
          height: 6.2,
          alignment: 'left',
          fontSize: 11,
          characterSpacing: 0,
          lineHeight: 1
        },
        construct_code: {
          // 工事No
          type: 'text',
          position: {
            x: 200,
            y: 21.69
          },
          width: 60,
          height: 4.6,
          alignment: 'left',
          fontSize: 11,
          characterSpacing: 0,
          lineHeight: 1
        },
        code: {
          // 見積書No
          type: 'text',
          position: {
            x: 200,
            y: 29.04
          },
          width: 60,
          height: 4.6,
          alignment: 'left',
          fontSize: 11,
          characterSpacing: 0,
          lineHeight: 1
        },
        estimate_date: {
          // 見積書日付
          type: 'text',
          position: {
            x: 200,
            y: 36.39
          },
          width: 60,
          height: 4.6,
          alignment: 'left',
          fontSize: 11,
          characterSpacing: 0,
          lineHeight: 1
        },
        company_name: {
          // 会社名23文字以内
          type: 'text',
          position: {
            x: 180,
            y: 106.16
          },
          width: 80,
          height: 6.13,
          alignment: 'left',
          dynamicFontSize: {
            max: 15,
            min: 9.5
          },
          characterSpacing: 0,
          lineHeight: 1
        },
        company_name_long: {
          // 会社名の長さで、フィールドの位置を切り替える
          type: 'text',
          position: {
            x: 180,
            y: 100.16
          },
          width: 78,
          height: 6.13,
          alignment: 'left',
          fontSize: 14,
          characterSpacing: 0,
          lineHeight: 1
        },
        company_info: {
          // 会社情報
          type: 'text',
          position: {
            x: 180,
            y: 114.38
          },
          width: 120,
          height: 41.14,
          alignment: 'left',
          fontSize: 10.5,
          characterSpacing: 0,
          lineHeight: 1
        },
        estimate_conditions: {
          // 御見積条件
          type: 'text',
          position: {
            x: 182.12,
            y: 59.61
          },
          width: 74.56,
          height: 35.58,
          alignment: 'left',
          fontSize: 11,
          characterSpacing: 0,
          lineHeight: 1
        },
        companyStamp: {
          // 社印
          type: 'image',
          position: {
            x: 230.88,
            y: 100.25
          },
          width: 15,
          height: 15
        },
        companyLogo: {
          // 会社ロゴ
          type: 'image',
          position: {
            x: 200,
            y: 4
          },
          width: data.company_logo_width ? data.company_logo_width : 0,
          height: data.company_logo_height ? data.company_logo_height : 0
        }
      }
    ]
  };

  let billing_total_label = '';
  let billing_total = '';
  let total_price_label = '';
  let total_price = '';
  if (data.prioritize_include_tax == 1) {
    billing_total_label = '税込合計金額';
    billing_total = Number(data.total_price).toLocaleString('ja-JP');
    total_price_label = '本体工事金額';
    total_price = data.billing_total.toLocaleString('ja-JP');
  } else {
    billing_total_label = '本体工事金額';
    billing_total = Number(data.billing_total).toLocaleString('ja-JP');
    total_price_label = '税込合計金額';
    total_price = data.total_price.toLocaleString('ja-JP');
  }

  let company_info = `〒${data.company_postal_code.slice(
    0,
    3
  )} - ${data.company_postal_code.slice(-4)}
  ${data.company_address}\n\n`;

  if (data.construction_license) {
    company_info += `建設業許可: ${data.construction_license}\n\n`;
  }
  company_info += `TEL: ${data.company_phone_number}\n`;
  company_info += data.company_fax_number
    ? `FAX: ${data.company_fax_number}\n\n`
    : '\n';

  if (data.output_pdf) {
    company_info += `営業担当: ${data.sales_user_name}\n`;
    company_info += data.sales_user_tel
      ? `携帯電話: ${data.sales_user_tel}\n`
      : '';
  } else {
    company_info += `\n`;
  }

  let is_multiline_construct_name = false;

  if (window.location.pathname.includes('/received')) {
    if (data.construct_name.length > 25) {
      is_multiline_construct_name = true;
    }
  } else {
    if (data.construct_sub.length > 25 || /[\r\n]/.test(data.construct_sub)) {
      is_multiline_construct_name = true; // 25文字以上 or 改行あり
    }
  }

  // 現場住所のを複数行で表示するか
  let site_address_multi_line = false;

  if (data.site_address.length > 30) {
    site_address_multi_line = true;
  }

  // 会社名が長い場合フラグを持たせる
  let long_company_name_flg = false;

  if (data.company_name.length > 23) {
    long_company_name_flg = true;
  }

  let inputs: any = [];

  inputs = [
    {
      companyStamp:
        data.company_stamp && data.company_stamp !== ''
          ? `data:${data.company_stamp_format};base64,${data.company_stamp}`
          : '',
      companyLogo:
        data.company_logo && data.company_logo !== ''
          ? `data:${data.company_logo_format};base64,${data.company_logo}`
          : '',
      client_name: `${data.client_name} ${data.honorific_title}`,
      billing_total_label: billing_total_label,
      billing_total: billing_total,
      total_price_label: total_price_label,
      total_price: total_price,
      tax: data.tax.toLocaleString('ja-JP'),
      construct_sub: window.location.pathname.includes('/received')
        ? data.construct_name
        : data.construct_sub,
      summary: data.summary,
      site_address: site_address_multi_line ? '' : data.site_address,
      site_address_under_30: site_address_multi_line
        ? data.site_address.substr(0, 34)
        : '',
      site_address_over_30: site_address_multi_line
        ? data.site_address.substr(34, 36)
        : '',
      start_date: data.expected_start_date,
      end_date: data.expected_end_date,
      expire_date: data.expire_date,
      terms_of_trade: data.terms_of_trade,
      construct_code: `工事No. ${data.construct_code}`,
      code: `見積書No. ${data.code}`,
      estimate_date: `見積書日付 ${
        window.location.pathname.includes('/received')
          ? data.submission_date
          : data.submission_date_str
      }`,
      company_name: long_company_name_flg ? '' : data.company_name,
      company_name_long: long_company_name_flg ? data.company_name : '',
      company_info: company_info,
      estimate_conditions: data.estimate_conditions
    }
  ];

  const pdf = await generatePdfUint8Array(template, inputs, font);

  return pdf;
};

const getPage2 = async (data: any, font: any) => {
  // 初回読み込みの容量を減らすため動的インポート
  const basePdf2: any = await import('../assets/estimates_base_pdf2');

  // 階層別に振り分け
  const mainItems = data.items.filter((item: TEstimateBaseItem) => {
    return item.layer === 1;
  });
  const allSecondItems = data.items.filter((item: TEstimateBaseItem) => {
    return item.layer === 2;
  });
  const allThirdItems = data.items.filter((item: TEstimateBaseItem) => {
    return item.layer === 3;
  });
  const allFourthItems = data.items.filter((item: TEstimateBaseItem) => {
    return item.layer === 4;
  });
  const allFifthItems = data.items.filter((item: TEstimateBaseItem) => {
    return item.layer === 5;
  });

  // テンプレート
  const schemas: any = {};
  // 項目書
  const itemInputs: any = [];
  // 明細書
  const secondlItemInputs: any = [];

  schemas['code'] = {
    // NO
    type: 'text',
    position: {
      x: 8.9,
      y: 10.06
    },
    width: 59.87,
    height: 6.2,
    alignment: 'left',
    fontSize: 10,
    characterSpacing: 0,
    lineHeight: 1.5
  };
  schemas['title'] = {
    // 名称
    type: 'text',
    position: {
      x: 114.3,
      y: 14.06
    },
    width: 66.48,
    height: 4.88,
    alignment: 'center',
    fontSize: 10,
    characterSpacing: 0,
    lineHeight: 1.5
  };
  schemas['total_price'] = {
    // 小計
    type: 'text',
    position: {
      x: 181,
      y: 193.94
    },
    width: 40,
    height: 6.2,
    alignment: 'right',
    fontSize: 10,
    characterSpacing: 0,
    lineHeight: 1
  };
  schemas[`breadcrumbs`] = {
    type: 'text',
    position: {
      x: 13.2,
      y: 20.64
    },
    width: 240,
    height: 4.5,
    alignment: 'left',
    fontSize: 9,
    characterSpacing: 0,
    lineHeight: 1.9
  };
  schemas['page_no'] = {
    type: 'text',
    position: {
      x: 192,
      y: 203
    },
    width: 100,
    height: 6.2,
    alignment: 'right',
    fontSize: 10,
    characterSpacing: 0,
    lineHeight: 1
  };
  [...Array(24)].forEach((_: any, i: number) => {
    schemas[`item_no${i}`] = {
      type: 'text',
      position: {
        x: 10.2,
        y: 36 + 6.55 * i
      },
      width: 14,
      height: 4.5,
      alignment: 'center',
      fontSize: 9,
      characterSpacing: 0,
      lineHeight: 1.9
    };
    schemas[`item_name${i}`] = {
      type: 'text',
      position: {
        x: 23.1,
        y: 36.5 + 6.55 * i
      },
      width: 66,
      height: 6.4,
      alignment: 'left',
      fontSize: 9,
      characterSpacing: -0.1,
      lineHeight: 1.9
    };
    // Schema when the number of characters is 20 or more
    // first line
    schemas[`item_spec${i}_top`] = {
      type: 'text',
      position: {
        x: 86.5,
        y: 35.5 + 6.55 * i
      },
      width: 66,
      height: 6.4,
      alignment: 'left',
      fontSize: 9,
      characterSpacing: -0.1,
      lineHeight: 1.5
    };
    // 2nd line
    schemas[`item_spec${i}_seconds`] = {
      type: 'text',
      position: {
        x: 86.5,
        y: 38.15 + 6.55 * i
      },
      width: 66,
      height: 6.4,
      alignment: 'left',
      fontSize: 9,
      characterSpacing: -0.1,
      lineHeight: 1.5
    };
    schemas[`item_spec${i}`] = {
      type: 'text',
      position: {
        x: 86.5,
        y: 36.5 + 6.55 * i
      },
      width: 66,
      height: 6.4,
      alignment: 'left',
      fontSize: 9,
      characterSpacing: -0.1,
      lineHeight: 1.9
    };
    schemas[`item_quantity${i}`] = {
      type: 'text',
      position: {
        x: 152,
        y: 36 + 6.55 * i
      },
      width: 12.5,
      height: 6.4,
      alignment: 'center',
      fontSize: 9,
      characterSpacing: 0,
      lineHeight: 1.9
    };
    schemas[`item_unit${i}`] = {
      type: 'text',
      position: {
        x: 165,
        y: 36 + 6.55 * i
      },
      width: 14,
      height: 6.4,
      alignment: 'center',
      fontSize: 9,
      characterSpacing: 0,
      lineHeight: 1.9
    };
    schemas[`item_unit_price${i}`] = {
      type: 'text',
      position: {
        x: 173,
        y: 36 + 6.55 * i
      },
      width: 26,
      height: 6.4,
      alignment: 'right',
      fontSize: 9,
      characterSpacing: 0,
      lineHeight: 1.9
    };
    schemas[`item_all_selling_price${i}`] = {
      type: 'text',
      position: {
        x: 195,
        y: 36 + 6.55 * i
      },
      width: 26,
      height: 6.4,
      alignment: 'right',
      fontSize: 9,
      characterSpacing: 0,
      lineHeight: 1.9
    };
    schemas[`item_remarks${i}_top`] = {
      type: 'text',
      position: {
        x: 223,
        y: 35.5 + 6.55 * i
      },
      width: 86,
      height: 6.4,
      alignment: 'left',
      fontSize: 9,
      characterSpacing: -0.1,
      lineHeight: 1.5
    };
    schemas[`item_remarks${i}_seconds`] = {
      type: 'text',
      position: {
        x: 223,
        y: 38.15 + 6.55 * i
      },
      width: 86,
      height: 6.4,
      alignment: 'left',
      fontSize: 9,
      characterSpacing: -0.1,
      lineHeight: 1.5
    };
    schemas[`item_remarks${i}`] = {
      type: 'text',
      position: {
        x: 223,
        y: 36.5 + 6.55 * i
      },
      width: 86,
      height: 6.4,
      alignment: 'left',
      fontSize: 9,
      characterSpacing: -0.1,
      lineHeight: 1.9
    };
  });

  // 第一階層の表示に必要なページ数
  const pageNum = Math.ceil(mainItems.length / ESTIMATE_ITEM_MAX_PER_PDF_PAGE);

  // ページ番号
  let pageNo = 0;

  // タイトル、見積書番号、ページ番号、「次ページに続く」を第一階層のページに追加
  for (let i = 0; i < pageNum; i++) {
    pageNo += 1;
    itemInputs.push({
      title: '＜御　見　積　項　目　書＞',
      code: `見積書No. ${data.code}`,
      page_no: String(pageNo),
      total_price: '次ページに続く'
    });
  }

  // 第一階層の小計
  let items_total_price = 0;

  mainItems.forEach((item: TEstimateBaseItem, i: number) => {
    // 1ページのオブジェクト
    const firstObj = itemInputs[Math.floor(i / ESTIMATE_ITEM_MAX_PER_PDF_PAGE)];

    // 1ページ内でのインデックス
    const indexInSheet = i % ESTIMATE_ITEM_MAX_PER_PDF_PAGE;

    // 第二階層の小計
    let second_items_total_price = 0;

    firstObj[`item_no${indexInSheet}`] = data.display_item_number_in_pdf
      ? String(i + 1)
      : '';
    firstObj[`item_name${indexInSheet}`] = toPdfStr(item.name);
    const Quantity = item.quantity
      ? Number(item.quantity).toLocaleString('ja-JP')
      : '';
    firstObj[`item_quantity${indexInSheet}`] = Quantity;
    firstObj[`item_unit${indexInSheet}`] = item.unit_name;
    const itemSellingUnitPrice =
      item.selling_unit_price != 0
        ? Number(item.selling_unit_price).toLocaleString('ja-JP')
        : '';
    firstObj[`item_unit_price${indexInSheet}`] = itemSellingUnitPrice;
    // 小数点以下切り捨て
    const itemAllSellingPrice: number = calcAllSellingPrice(
      Number(item.selling_unit_price),
      Number(item.quantity)
    );
    if (itemAllSellingPrice != 0) {
      firstObj[`item_all_selling_price${indexInSheet}`] =
        itemAllSellingPrice.toLocaleString('ja-JP');
    } else {
      firstObj[`item_all_selling_price${indexInSheet}`] = '';
    }
    // spec format
    const itemSpec: string = toPdfStr(item.spec);
    const specIndex = itemSpec.search(/\r\n|\n|\r/);
    if (specIndex !== -1) {
      // 改行が含まれる場合
      firstObj[`item_spec${indexInSheet}_top`] = itemSpec.substring(
        0,
        specIndex
      );
      firstObj[`item_spec${indexInSheet}_seconds`] = itemSpec.substring(
        specIndex + 1
      );
    } else if (itemSpec.length > 20) {
      // 改行が含まれず、文字数が20文字を超える場合
      firstObj[`item_spec${indexInSheet}_top`] = itemSpec.substr(0, 20);
      firstObj[`item_spec${indexInSheet}_seconds`] = itemSpec.substr(20, 20);
    } else {
      // それ以外の場合（20文字以下）
      firstObj[`item_spec${indexInSheet}`] = itemSpec;
    }
    //remarks format
    const itemRemarks: string = toPdfStr(item.remarks);
    const remarksIndex = itemRemarks.search(/\r\n|\n|\r/);
    if (remarksIndex !== -1) {
      // 改行が含まれる場合
      firstObj[`item_remarks${indexInSheet}_top`] = itemRemarks.substring(
        0,
        remarksIndex
      );
      firstObj[`item_remarks${indexInSheet}_seconds`] = itemRemarks.substring(
        remarksIndex + 1
      );
    } else if (itemRemarks.length > 20) {
      // 改行が含まれず、文字数が20文字を超える場合
      firstObj[`item_remarks${indexInSheet}_top`] = itemRemarks.substr(0, 20);
      firstObj[`item_remarks${indexInSheet}_seconds`] = itemRemarks.substr(
        20,
        20
      );
    } else {
      // それ以外の場合（20文字以下）
      firstObj[`item_remarks${indexInSheet}`] = itemRemarks;
    }

    // 明細書 - 第二階層
    if (item.has_under_layer_flg) {
      //１階層が明細ありの場合、idが位置する２階層を取得
      const secondItems = allSecondItems.filter(
        (allSecondItem: TEstimateBaseItem) => {
          return allSecondItem.parent_item_id === item.id;
        }
      );
      if (secondItems.length > 0) {
        // 第二階層を表示するのに必要な枚数
        const secondPageNum = Math.ceil(
          secondItems.length / ESTIMATE_ITEM_MAX_PER_PDF_PAGE
        );

        // 第二階層を表示するのに必要な枚数のシートを用意する。
        const secondSheets: any = [];
        for (let k = 0; k < secondPageNum; k++) {
          pageNo += 1;
          secondSheets.push({
            title: '＜御　見　積　明　細　書＞',
            code: `見積書No. ${data.code}`,
            // パンくず
            breadcrumbs: `${String(i + 1)}. ${item.name}`,
            page_no: String(pageNo),
            total_price: '次ページに続く'
          });
        }

        // 三階層格納用
        const thirdItemInputs: any = [];

        secondItems.forEach((secondItem: any, second_i: number) => {
          // 1ページのオブジェクト
          const secondObj =
            secondSheets[Math.floor(second_i / ESTIMATE_ITEM_MAX_PER_PDF_PAGE)];

          // 1ページ内でのインデックス
          const secondIndexInSheet = second_i % ESTIMATE_ITEM_MAX_PER_PDF_PAGE;

          secondObj[`item_no${secondIndexInSheet}`] =
            data.display_item_number_in_pdf ? String(second_i + 1) : '';
          secondObj[`item_name${secondIndexInSheet}`] = toPdfStr(
            secondItem.name
          );
          const secondSpec: string = toPdfStr(secondItem.spec);
          // spec format
          const secondSpecIndex = secondSpec.search(/\r\n|\n|\r/);
          if (secondSpecIndex !== -1) {
            // 改行が含まれる場合
            secondObj[`item_spec${secondIndexInSheet}_top`] =
              secondSpec.substring(0, secondSpecIndex);
            secondObj[`item_spec${secondIndexInSheet}_seconds`] =
              secondSpec.substring(secondSpecIndex + 1);
          } else if (secondSpec.length > 20) {
            // Processing when the number of characters is 20 or more
            secondObj[`item_spec${secondIndexInSheet}_top`] = secondSpec.substr(
              0,
              20
            );
            secondObj[`item_spec${secondIndexInSheet}_seconds`] =
              secondSpec.substr(20, 20);
          } else {
            secondObj[`item_spec${secondIndexInSheet}`] = secondSpec;
          }
          const secondQuantity = secondItem.quantity
            ? Number(secondItem.quantity).toLocaleString('ja-JP')
            : '';
          secondObj[`item_quantity${secondIndexInSheet}`] = secondQuantity;
          secondObj[`item_unit${secondIndexInSheet}`] = secondItem.unit_name;
          const secondItemSellingUnitPrice =
            secondItem.selling_unit_price != 0
              ? Number(secondItem.selling_unit_price).toLocaleString('ja-JP')
              : '';
          secondObj[`item_unit_price${secondIndexInSheet}`] =
            secondItemSellingUnitPrice;
          // 小数点以下切り捨て
          const secondItemAllSellingPrice: number = calcAllSellingPrice(
            Number(secondItem.selling_unit_price),
            Number(secondItem.quantity)
          );
          if (secondItemAllSellingPrice != 0) {
            secondObj[`item_all_selling_price${secondIndexInSheet}`] =
              secondItemAllSellingPrice.toLocaleString('ja-JP');
          } else {
            secondObj[`item_all_selling_price${secondIndexInSheet}`] = '';
          }
          const secondRemarks: string = toPdfStr(secondItem.remarks);
          // remarks format
          const secondRemarksIndex = secondRemarks.search(/\r\n|\n|\r/);
          if (secondRemarksIndex !== -1) {
            // 改行が含まれる場合
            secondObj[`item_remarks${secondIndexInSheet}_top`] =
              secondRemarks.substring(0, secondRemarksIndex);
            secondObj[`item_remarks${secondIndexInSheet}_seconds`] =
              secondRemarks.substring(secondRemarksIndex + 1);
          } else if (secondRemarks.length > 20) {
            // Processing when the number of characters is 15 or more
            secondObj[`item_remarks${secondIndexInSheet}_top`] =
              secondRemarks.substr(0, 20);
            secondObj[`item_remarks${secondIndexInSheet}_seconds`] =
              secondRemarks.substr(20, 20);
          } else {
            secondObj[`item_remarks${secondIndexInSheet}`] = secondItem.remarks;
          }

          // 明細書 - 第三階層
          if (secondItem.has_under_layer_flg) {
            // ２階層が明細ありの場合、idが位置する３階層を取得
            const thirdItems = allThirdItems.filter(
              (allThirdItem: TEstimateBaseItem) => {
                return allThirdItem.parent_item_id === secondItem.id;
              }
            );
            if (thirdItems.length > 0) {
              // 第三階層を表示するのに必要な枚数
              const thirdPageNum = Math.ceil(
                thirdItems.length / ESTIMATE_ITEM_MAX_PER_PDF_PAGE
              );

              // 第三階層を表示するのに必要な枚数のシートを用意する。
              const thirdSheets: any = [];
              for (let k = 0; k < thirdPageNum; k++) {
                pageNo += 1;
                thirdSheets.push({
                  title: '＜御　見　積　明　細　書＞',
                  code: `見積書No. ${data.code}`,
                  // パンくず
                  breadcrumbs: `${String(i + 1)}. ${item.name}　>　${String(
                    second_i + 1
                  )}. ${secondItem.name}`,
                  page_no: String(pageNo),
                  total_price: '次ページに続く'
                });
              }
              // 四階層格納用
              const fourthItemInputs: any = [];
              // 第三階層の小計
              let third_items_total_price = 0;

              thirdItems.forEach((thirdItem: any, third_i: number) => {
                // 1ページのオブジェクト
                const thirdObj =
                  thirdSheets[
                    Math.floor(third_i / ESTIMATE_ITEM_MAX_PER_PDF_PAGE)
                  ];

                // 1ページ内でのインデックス
                const thirdIndexInSheet =
                  third_i % ESTIMATE_ITEM_MAX_PER_PDF_PAGE;

                thirdObj[`item_no${thirdIndexInSheet}`] =
                  data.display_item_number_in_pdf ? String(third_i + 1) : '';
                thirdObj[`item_name${thirdIndexInSheet}`] = toPdfStr(
                  thirdItem.name
                );
                const thirdSpec: string = toPdfStr(thirdItem.spec);
                // spec format
                const thirdSpecIndex = thirdSpec.search(/\r\n|\n|\r/);
                if (thirdSpecIndex !== -1) {
                  // 改行が含まれる場合
                  thirdObj[`item_spec${thirdIndexInSheet}_top`] =
                    thirdSpec.substring(0, thirdSpecIndex);
                  thirdObj[`item_spec${thirdIndexInSheet}_seconds`] =
                    thirdSpec.substring(thirdSpecIndex + 1);
                } else if (thirdSpec.length > 20) {
                  // Processing when the number of characters is 20 or more
                  thirdObj[`item_spec${thirdIndexInSheet}_top`] =
                    thirdSpec.substr(0, 20);
                  thirdObj[`item_spec${thirdIndexInSheet}_seconds`] =
                    thirdSpec.substr(20, 20);
                } else {
                  thirdObj[`item_spec${thirdIndexInSheet}`] = thirdSpec;
                }
                const thirdQuantity = thirdItem.quantity
                  ? Number(thirdItem.quantity).toLocaleString('ja-JP')
                  : '';
                thirdObj[`item_quantity${thirdIndexInSheet}`] = thirdQuantity;
                thirdObj[`item_unit${thirdIndexInSheet}`] = thirdItem.unit_name;
                const thirdItemSellingUnitPrice =
                  thirdItem.selling_unit_price != 0
                    ? Number(thirdItem.selling_unit_price).toLocaleString(
                        'ja-JP'
                      )
                    : '';
                thirdObj[`item_unit_price${thirdIndexInSheet}`] =
                  thirdItemSellingUnitPrice;
                // 小数点以下切り捨て
                const thirdItemAllSellingPrice: number = calcAllSellingPrice(
                  Number(thirdItem.selling_unit_price),
                  Number(thirdItem.quantity)
                );
                if (thirdItemAllSellingPrice != 0) {
                  thirdObj[`item_all_selling_price${thirdIndexInSheet}`] =
                    thirdItemAllSellingPrice.toLocaleString('ja-JP');
                } else {
                  thirdObj[`item_all_selling_price${thirdIndexInSheet}`] = '';
                }
                const thirdRemarks: string = toPdfStr(thirdItem.remarks);
                // remarks format
                const thirdRemarksIndex = thirdRemarks.search(/\r\n|\n|\r/);
                if (thirdRemarksIndex !== -1) {
                  // 改行が含まれる場合
                  thirdObj[`item_remarks${thirdIndexInSheet}_top`] =
                    thirdRemarks.substring(0, thirdRemarksIndex);
                  thirdObj[`item_remarks${thirdIndexInSheet}_seconds`] =
                    thirdRemarks.substring(thirdRemarksIndex + 1);
                } else if (thirdRemarks.length > 20) {
                  // Processing when the number of characters is 15 or more
                  thirdObj[`item_remarks${thirdIndexInSheet}_top`] =
                    thirdRemarks.substr(0, 20);
                  thirdObj[`item_remarks${thirdIndexInSheet}_seconds`] =
                    thirdRemarks.substr(20, 20);
                } else {
                  thirdObj[`item_remarks${thirdIndexInSheet}`] = thirdRemarks;
                }

                // 明細書 - 第四階層
                if (thirdItem.has_under_layer_flg) {
                  // ３階層が明細ありの場合、idが位置する４階層を取得
                  const fourthItems = allFourthItems.filter(
                    (allFourthItem: TEstimateBaseItem) => {
                      return allFourthItem.parent_item_id === thirdItem.id;
                    }
                  );
                  if (fourthItems.length > 0) {
                    // 第四階層を表示するのに必要な枚数
                    const fourthPageNum = Math.ceil(
                      fourthItems.length / ESTIMATE_ITEM_MAX_PER_PDF_PAGE
                    );

                    // 第四階層を表示するのに必要な枚数のシートを用意する。
                    const fourthSheets: any = [];
                    for (let k = 0; k < fourthPageNum; k++) {
                      pageNo += 1;
                      fourthSheets.push({
                        title: '＜御　見　積　明　細　書＞',
                        code: `見積書No. ${data.code}`,
                        // パンくず
                        breadcrumbs: `${String(i + 1)}. ${
                          item.name
                        }　>　${String(second_i + 1)}. ${
                          secondItem.name
                        }　>　${String(third_i + 1)}. ${thirdItem.name}`,
                        page_no: String(pageNo),
                        total_price: '次ページに続く'
                      });
                    }
                    // 五階層格納用
                    const fifthItemInputs: any = [];
                    // 第四階層の小計
                    let fourth_items_total_price = 0;

                    fourthItems.forEach((fourthItem: any, fourth_i: number) => {
                      // 1ページのオブジェクト
                      const fourthObj =
                        fourthSheets[
                          Math.floor(fourth_i / ESTIMATE_ITEM_MAX_PER_PDF_PAGE)
                        ];

                      // 1ページ内でのインデックス
                      const fourthIndexInSheet =
                        fourth_i % ESTIMATE_ITEM_MAX_PER_PDF_PAGE;

                      fourthObj[`item_no${fourthIndexInSheet}`] =
                        data.display_item_number_in_pdf
                          ? String(fourth_i + 1)
                          : '';
                      fourthObj[`item_name${fourthIndexInSheet}`] = toPdfStr(
                        fourthItem.name
                      );
                      const fourthSpec: string = toPdfStr(fourthItem.spec);
                      // spec format
                      const fourthSpecIndex = fourthSpec.search(/\r\n|\n|\r/);
                      if (fourthSpecIndex !== -1) {
                        // 改行が含まれる場合
                        fourthObj[`item_spec${fourthIndexInSheet}_top`] =
                          fourthSpec.substring(0, fourthSpecIndex);
                        fourthObj[`item_spec${fourthIndexInSheet}_seconds`] =
                          fourthSpec.substring(fourthSpecIndex + 1);
                      } else if (fourthSpec.length > 20) {
                        // Processing when the number of characters is 20 or more
                        fourthObj[`item_spec${fourthIndexInSheet}_top`] =
                          fourthSpec.substr(0, 20);
                        fourthObj[`item_spec${fourthIndexInSheet}_seconds`] =
                          fourthSpec.substr(20, 20);
                      } else {
                        fourthObj[`item_spec${fourthIndexInSheet}`] =
                          fourthSpec;
                      }
                      const fourthQuantity = fourthItem.quantity
                        ? Number(fourthItem.quantity).toLocaleString('ja-JP')
                        : '';
                      fourthObj[`item_quantity${fourthIndexInSheet}`] =
                        fourthQuantity;
                      fourthObj[`item_unit${fourthIndexInSheet}`] =
                        fourthItem.unit_name;
                      const fourthItemSellingUnitPrice =
                        fourthItem.selling_unit_price != 0
                          ? Number(
                              fourthItem.selling_unit_price
                            ).toLocaleString('ja-JP')
                          : '';
                      fourthObj[`item_unit_price${fourthIndexInSheet}`] =
                        fourthItemSellingUnitPrice;
                      // 小数点以下切り捨て
                      const fourthItemAllSellingPrice: number =
                        calcAllSellingPrice(
                          Number(fourthItem.selling_unit_price),
                          Number(fourthItem.quantity)
                        );
                      if (fourthItemAllSellingPrice != 0) {
                        fourthObj[
                          `item_all_selling_price${fourthIndexInSheet}`
                        ] = fourthItemAllSellingPrice.toLocaleString('ja-JP');
                      } else {
                        fourthObj[
                          `item_all_selling_price${fourthIndexInSheet}`
                        ] = '';
                      }
                      const fourthRemarks: string = toPdfStr(
                        fourthItem.remarks
                      );
                      // remarks format
                      const fourthRemarksIndex =
                        fourthRemarks.search(/\r\n|\n|\r/);
                      if (fourthRemarksIndex !== -1) {
                        // 改行が含まれる場合
                        fourthObj[`item_remarks${fourthIndexInSheet}_top`] =
                          fourthRemarks.substring(0, fourthRemarksIndex);
                        fourthObj[`item_remarks${fourthIndexInSheet}_seconds`] =
                          fourthRemarks.substring(fourthRemarksIndex + 1);
                      } else if (fourthItem.remarks.length > 20) {
                        // Processing when the number of characters is 15 or more
                        fourthObj[`item_remarks${fourthIndexInSheet}_top`] =
                          fourthRemarks.substr(0, 20);
                        fourthObj[`item_remarks${fourthIndexInSheet}_seconds`] =
                          fourthRemarks.substr(20, 20);
                      } else {
                        fourthObj[`item_remarks${fourthIndexInSheet}`] =
                          fourthRemarks;
                      }

                      // 明細書 - 第五階層
                      if (fourthItem.has_under_layer_flg) {
                        // ４階層が明細ありの場合、idが位置する５階層を取得
                        const fifthItems = allFifthItems.filter(
                          (allFifthItem: TEstimateBaseItem) => {
                            return (
                              allFifthItem.parent_item_id === fourthItem.id
                            );
                          }
                        );
                        if (fifthItems.length > 0) {
                          // 第五階層を表示するのに必要な枚数
                          const fifthPageNum = Math.ceil(
                            fifthItems.length / ESTIMATE_ITEM_MAX_PER_PDF_PAGE
                          );

                          // 第五階層を表示するのに必要な枚数のシートを用意する。
                          const fifthSheets: any = [];
                          for (let k = 0; k < fifthPageNum; k++) {
                            pageNo += 1;
                            fifthSheets.push({
                              title: '＜御　見　積　明　細　書＞',
                              code: `見積書No. ${data.code}`,
                              breadcrumbs: `${String(i + 1)}. ${
                                item.name
                              }　>　${String(second_i + 1)}. ${
                                secondItem.name
                              }　>　${String(third_i + 1)}. ${
                                thirdItem.name
                              } 　>　 ${String(fourth_i + 1)}. ${
                                fourthItem.name
                              }`,
                              page_no: String(pageNo),
                              total_price: '次ページに続く'
                            });
                          }
                          // 第五階層の小計
                          let fifth_items_total_price = 0;

                          fifthItems.forEach(
                            (fifthItem: any, fifth_i: number) => {
                              // 1ページのオブジェクト
                              const fifthObj =
                                fifthSheets[
                                  Math.floor(
                                    fifth_i / ESTIMATE_ITEM_MAX_PER_PDF_PAGE
                                  )
                                ];

                              // 1ページ内でのインデックス
                              const fifthIndexInSheet =
                                fifth_i % ESTIMATE_ITEM_MAX_PER_PDF_PAGE;

                              fifthObj[`item_no${fifthIndexInSheet}`] =
                                data.display_item_number_in_pdf
                                  ? String(fifth_i + 1)
                                  : '';
                              fifthObj[`item_name${fifthIndexInSheet}`] =
                                toPdfStr(fifthItem.name);
                              const fifthSpec: string = toPdfStr(
                                fifthItem.spec
                              );
                              // spec format
                              const fifthSpecIndex =
                                fifthSpec.search(/\r\n|\n|\r/);
                              if (fifthSpecIndex !== -1) {
                                // 改行が含まれる場合
                                fifthObj[`item_spec${fifthIndexInSheet}_top`] =
                                  fifthSpec.substring(0, fifthSpecIndex);
                                fifthObj[
                                  `item_spec${fifthIndexInSheet}_seconds`
                                ] = fifthSpec.substring(fifthSpecIndex + 1);
                              } else if (fifthSpec.length > 20) {
                                // Processing when the number of characters is 20 or more
                                fifthObj[`item_spec${fifthIndexInSheet}_top`] =
                                  fifthSpec.substr(0, 20);
                                fifthObj[
                                  `item_spec${fifthIndexInSheet}_seconds`
                                ] = fifthSpec.substr(20, 20);
                              } else {
                                fifthObj[`item_spec${fifthIndexInSheet}`] =
                                  fifthSpec;
                              }
                              const fifthQuantity = fifthItem.quantity
                                ? Number(fifthItem.quantity).toLocaleString(
                                    'ja-JP'
                                  )
                                : '';
                              fifthObj[`item_quantity${fifthIndexInSheet}`] =
                                fifthQuantity;
                              fifthObj[`item_unit${fifthIndexInSheet}`] =
                                fifthItem.unit_name;
                              const fifthItemSellingUnitPrice =
                                fifthItem.selling_unit_price != 0
                                  ? Number(
                                      fifthItem.selling_unit_price
                                    ).toLocaleString('ja-JP')
                                  : '';
                              fifthObj[`item_unit_price${fifthIndexInSheet}`] =
                                fifthItemSellingUnitPrice;
                              // 小数点以下切り捨て
                              const fifthItemAllSellingPrice: number =
                                calcAllSellingPrice(
                                  Number(fifthItem.selling_unit_price),
                                  Number(fifthItem.quantity)
                                );
                              if (fifthItemAllSellingPrice != 0) {
                                fifthObj[
                                  `item_all_selling_price${fifthIndexInSheet}`
                                ] =
                                  fifthItemAllSellingPrice.toLocaleString(
                                    'ja-JP'
                                  );
                              } else {
                                fifthObj[
                                  `item_all_selling_price${fifthIndexInSheet}`
                                ] = '';
                              }
                              const fifthRemarks: string = toPdfStr(
                                fifthItem.remarks
                              );
                              // remarks format
                              const fifthRemarksIndex =
                                fifthRemarks.search(/\r\n|\n|\r/);
                              if (fifthRemarksIndex !== -1) {
                                // 改行が含まれる場合
                                fifthObj[
                                  `item_remarks${fifthIndexInSheet}_top`
                                ] = fifthRemarks.substring(
                                  0,
                                  fifthRemarksIndex
                                );
                                fifthObj[
                                  `item_remarks${fifthIndexInSheet}_seconds`
                                ] = fifthRemarks.substring(
                                  fifthRemarksIndex + 1
                                );
                              } else if (fifthRemarks.length > 20) {
                                // Processing when the number of characters is 15 or more
                                fifthObj[
                                  `item_remarks${fifthIndexInSheet}_top`
                                ] = fifthRemarks.substr(0, 20);
                                fifthObj[
                                  `item_remarks${fifthIndexInSheet}_seconds`
                                ] = fifthRemarks.substr(20, 20);
                              } else {
                                fifthObj[`item_remarks${fifthIndexInSheet}`] =
                                  fifthRemarks;
                              }
                              fifth_items_total_price +=
                                fifthItemAllSellingPrice;
                            }
                          );
                          // 第五階層小計
                          fifthSheets[fifthSheets.length - 1]['total_price'] =
                            '小計　　' +
                            Number(fifth_items_total_price).toLocaleString(
                              'ja-JP'
                            );
                          fifthItemInputs.push(...fifthSheets);

                          // 四階層の金額更新
                          const newFourthItemAllSellingPrice =
                            calcAllSellingPrice(
                              fifth_items_total_price,
                              fourthItem.quantity
                            );
                          fourthObj[
                            `item_all_selling_price${fourthIndexInSheet}`
                          ] =
                            newFourthItemAllSellingPrice !== 0
                              ? newFourthItemAllSellingPrice.toLocaleString(
                                  'ja-JP'
                                )
                              : '';
                          fourth_items_total_price +=
                            newFourthItemAllSellingPrice;
                        }
                      } else {
                        // 五階層を持たない場合は（単価*数量）を小計に加える
                        fourth_items_total_price += fourthItemAllSellingPrice;
                      }
                    });
                    // 第四階層小計
                    fourthSheets[fourthSheets.length - 1]['total_price'] =
                      '小計　　' +
                      Number(fourth_items_total_price).toLocaleString('ja-JP');
                    fourthItemInputs.push(...fourthSheets, ...fifthItemInputs);

                    // 三階層の金額更新
                    const newThirdItemAllSellingPrice = calcAllSellingPrice(
                      fourth_items_total_price,
                      thirdItem.quantity
                    );
                    thirdObj[`item_all_selling_price${thirdIndexInSheet}`] =
                      newThirdItemAllSellingPrice !== 0
                        ? newThirdItemAllSellingPrice.toLocaleString('ja-JP')
                        : '';
                    third_items_total_price += newThirdItemAllSellingPrice;
                  }
                } else {
                  // 四階層を持たない場合は（単価*数量）を小計に加える
                  third_items_total_price += thirdItemAllSellingPrice;
                }
              });
              // 第三階層小計
              thirdSheets[thirdSheets.length - 1]['total_price'] =
                '小計　　' +
                Number(third_items_total_price).toLocaleString('ja-JP');
              thirdItemInputs.push(...thirdSheets, ...fourthItemInputs);

              // 二階層の金額更新
              const newSecondItemAllSellingPrice = calcAllSellingPrice(
                third_items_total_price,
                secondItem.quantity
              );
              secondObj[`item_all_selling_price${secondIndexInSheet}`] =
                newSecondItemAllSellingPrice !== 0
                  ? newSecondItemAllSellingPrice.toLocaleString('ja-JP')
                  : '';
              second_items_total_price += newSecondItemAllSellingPrice;
            }
          } else {
            // 三階層を持たない場合は（単価*数量）を小計に加える
            second_items_total_price += secondItemAllSellingPrice;
          }
        });
        // 第二階層小計
        secondSheets[secondSheets.length - 1]['total_price'] =
          '小計　　' + Number(second_items_total_price).toLocaleString('ja-JP');
        secondlItemInputs.push(...secondSheets, ...thirdItemInputs);

        // 親階層の金額を更新し、小計に加える
        const newItemAllSellingPrice = calcAllSellingPrice(
          second_items_total_price,
          item.quantity
        );
        itemInputs[`item_all_selling_price${indexInSheet}`] =
          newItemAllSellingPrice !== 0
            ? newItemAllSellingPrice.toLocaleString('ja-JP')
            : '';
        items_total_price += newItemAllSellingPrice;
      }
    } else {
      // 二階層を持たない場合は（単価*数量）を小計に加える
      items_total_price += itemAllSellingPrice;
    }
  });

  // 第一階層小計
  itemInputs[itemInputs.length - 1]['total_price'] =
    '小計　　' + Number(items_total_price).toLocaleString('ja-JP');
  const template: Template = {
    // fontName: "mincho",
    basePdf: basePdf2.basePdf,
    schemas: [schemas]
  };

  const inputs = [...itemInputs, ...secondlItemInputs];
  const pdf = await generatePdfUint8Array(template, inputs, font);

  return pdf;
};

export const customPdf = async (
  data: any,
  customFormatPdf: null | string[]
) => {
  const mincho = await fetch('/yumin.ttf').then(res => res.arrayBuffer());
  const minchoBold = await fetch('/yumindb.ttf').then(res => res.arrayBuffer());
  const font = {
    mincho: { data: mincho, subset: true, fallback: true },
    minchoBold: { data: minchoBold, subset: true }
  };
  let blob = null;
  if (customFormatPdf) {
    const detailPdf1 = await import('../assets/custom_estimates_detail_pdf1');
    const detailPdf2 = await import('../assets/custom_estimates_detail_pdf2');
    // ユタカの場合、Trueとなる
    const isParentAmountHidden: boolean = false;
    const mergedPdf = await PDFDocument.create();
    const formatFiles = [];
    formatFiles.push(customFormatPdf[0]);
    formatFiles.push(customFormatPdf[1]);
    formatFiles.push(detailPdf1.basePdf);
    formatFiles.push(detailPdf2.basePdf);
    const pages: any = await createPdf(
      formatFiles,
      data,
      font,
      isParentAmountHidden
    );
    for (var i = 0; i < pages.length; i++) {
      const page = pages[i];
      const pdfDoc = await PDFDocument.load(page);
      const copiedPage = await mergedPdf.copyPages(
        pdfDoc,
        pdfDoc.getPageIndices()
      );
      copiedPage.forEach(page => mergedPdf.addPage(page));
    }

    const exportPdf = await mergedPdf.save();

    blob = new Blob([exportPdf], { type: 'application/pdf' });
  }
  return blob;
};

// 会社ロゴのスケーリング
const resizeImage = (srcWidth: number, srcHeight: number, maxSize: number) => {
  if (srcWidth && srcHeight) {
    const scale = maxSize / Math.max(srcWidth, srcHeight);
    return {
      width: Math.round(srcWidth * scale),
      height: Math.round(srcHeight * scale)
    };
  } else {
    return {
      width: maxSize,
      height: maxSize
    };
  }
};

export const customPdfFormat = async (
  logo: string,
  logo_format: string,
  logo_width: number,
  logo_height: number,
  stamp: string,
  stamp_format: string,
  tenant_id: number | null,
  isCreateThumbnail?: boolean
) => {
  // 初回読み込みの容量を減らすため動的インポート
  const basePdf1 = (await import('../assets/custom_estimates_summery_pdf1'))
    .basePdf;
  const basePdf2 = (await import('../assets/custom_estimates_summery_pdf2'))
    .basePdf;
  const logo_size = resizeImage(logo_width, logo_height, 21);

  // イテル建設様用に会社印の配置を変更する（その他の企業も追加可能）
  const companyLogCoordinate =
    typeof tenant_id === 'number' &&
    CUSTOM_ESTIMATE_STAMP_PLACE.prd.includes(tenant_id)
      ? { x: 165, y: 35 }
      : { x: 180, y: 39 };

  const createPdfBase64 = async (
    basePdf: string
  ): Promise<[string, Uint8Array]> => {
    const template: Template = {
      basePdf: basePdf,
      schemas: [
        {
          companyStamp: {
            type: 'image',
            position: companyLogCoordinate,
            width: 15,
            height: 15
          },
          companyLogo: {
            type: 'image',
            position: { x: 113, y: 35 },
            width: logo_size.width,
            height: logo_size.height
          }
        }
      ]
    };

    const inputs = [
      {
        companyStamp:
          stamp && stamp !== '' ? `data:${stamp_format};base64,${stamp}` : '',
        companyLogo:
          logo && logo !== '' ? `data:${logo_format};base64,${logo}` : ''
      }
    ];
    const pdfBinary = await generatePdfUint8Array(template, inputs);
    const blob = new Blob([pdfBinary], { type: 'application/pdf' });
    const blobToBase64 = (blob: Blob): Promise<string> => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64data = reader.result;
          if (typeof base64data === 'string') {
            resolve(base64data);
          } else {
            reject(new Error('Failed to convert Blob to Base64 string.'));
          }
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    };
    const base64String = await blobToBase64(blob);
    return [base64String, pdfBinary];
  };

  try {
    const [base64String1, pdfBinary] = await createPdfBase64(basePdf1);
    const [base64String2, _] = await createPdfBase64(basePdf2);

    const base64Strings = [base64String1, base64String2];

    // カスタム見積書のサムネイル作成
    if (isCreateThumbnail) {
      // PDFの解析・デコードをメインスレッドから切り離す
      GlobalWorkerOptions.workerSrc = new URL(
        'pdfjs-dist/legacy/build/pdf.worker.entry',
        import.meta.url
      ).toString();

      const loadingTask = getDocument({ data: pdfBinary });
      const pdf = await loadingTask.promise;
      const page = await pdf.getPage(1);
      const scale = 2.0;
      const viewport = page.getViewport({ scale });

      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d') as CanvasRenderingContext2D;
      canvas.width = viewport.width;
      canvas.height = viewport.height;

      const renderContext = {
        canvasContext: context,
        viewport: viewport
      };
      await page.render(renderContext).promise;

      const pngDataUrl = canvas.toDataURL('image/png');
      console.log(pngDataUrl);
      return { base64Strings, pngDataUrl };
    }
    return base64Strings;
  } catch (error) {
    // エラーが発生した際は、元のpdfとpngを返す
    console.error('Error during customPdf/thumbnail generation:', error);
    const base64Strings = [basePdf1, basePdf2];
    if (isCreateThumbnail) {
      const pngDataUrl = (await import('../assets/custom_estimates_thumbnail'))
        .basePng;
      return { base64Strings, pngDataUrl };
    }
    return base64Strings;
  }
};
